import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, Image } from '@/ui'
import { mtc } from '@/styles'
import { logoutUser } from '@/actions/authActions'
import { clearOpenFeatureTargetingKey } from '@/flags'
import { useAnalyticsReset } from '@/features/amplitude'
import { useAppDispatch } from '@/utils/typeHelpers'

import { FUNNEL_PAGE_CLS } from '../lib/constants'

/**
 * Wordmark-and-logout bar across the top of every funnel screen.
 *
 * The funnel drops the app sidebar, which is where logout lived, so this is a
 * prospect's only way out of a half-finished funnel. Deliberately the signup
 * flow's own header rather than something new — same wordmark, same link, same
 * row — since a prospect walks straight off those screens onto these, and the
 * seam would show.
 *
 * Sits at the funnel's own page width (`FUNNEL_PAGE_CLS`) rather than tracking
 * each screen's content column, which varies (intake `max-w-2xl`, plans
 * `max-w-3xl`, report `max-w-5xl`) — a wordmark that moved between screens
 * would read as three different headers.
 */
export const FunnelHeader: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const resetAnalytics = useAnalyticsReset()

  // The signup flow's logout, plus the targeting-key clear the app shell's
  // logout does and signup's omits: left set, the next person to sign up in this
  // browser is served flags targeted at the prospect who just left — and one of
  // those flags is what admits people to this funnel in the first place.
  const handleLogoutClick = useCallback(async () => {
    resetAnalytics()
    await logoutUser()(dispatch)
    localStorage.removeItem('targetLink')
    await clearOpenFeatureTargetingKey()
    navigate('/login')
  }, [dispatch, navigate, resetAnalytics])

  return (
    <div className={mtc(FUNNEL_PAGE_CLS, 'flex items-center justify-between')}>
      <Image
        src="https://heard-images.s3.us-west-1.amazonaws.com/heard_logo_green_2x.png"
        alt="heard logo"
        width={76}
      />
      {/* `-mr-4` cancels the button size's `px-4` on the right only, so the
          word sits flush with the page edge the wordmark starts from while the
          padding stays as hit area. */}
      <Button
        className="-mr-4 text-brand"
        variant="link"
        onClick={handleLogoutClick}
      >
        Logout
      </Button>
    </div>
  )
}
